import {
  ChainIndexTxOut,
  PubKeyHash,
  TxOutRef,
} from "./cardano";
import { WalletApi } from "lucid-cardano";
import {
  GYAddress,
  GYMintingPolicyId,
  GYTokenName,
  GYValue,
  RewardPool,
  UserInfo,
} from "../api/generated/models";
import { getMetadataPolicyIdTokenNameResponse } from "../api/generated/client";

/** Params to get reward pools by pool NFT asset class */
export type GetRewardPoolsByAssetParams = {
  policyId: string;
  tokenName: string;
};

/** Params used to withdraw reward from the reward pool. */
export type WithdrawRewardParams = {
  wallet: Wallet,
  kwarxsUser: UserInfo | null;
  /** Reward pool to withdraw from. */
  pool: RewardPool;
  /** Policy ID of the NFT collection. */
  collectionPolicyId: GYMintingPolicyId;
  /** List of NFTs to remint in order to withdraw. */
  nftTokenNames: GYTokenName[];
};

/** Decoded voting UTxO datum. */
export type VotingEscrowDatum = {
  /** List of user's votes */
  votes: [Proposal, Vote];
  /** PKH of users voting. */
  voter: PubKeyHash;
};

/** User's voting UTxO. */
export type UsersVote = {
  /** `TxOutRef` of the voting UTxO. */
  usersVoteTxOutRef: TxOutRef;
  /** `ChainIndexTxOut` of the voting UTxO. */
  usersVoteTxOut: ChainIndexTxOut;
  /** Decoded datum attached to voting UTxO. */
  usersVoteDatum: VotingEscrowDatum;
};

export type Vote = boolean;

export type Proposal = string;

export type VoteProposal = [Proposal, Vote];

export type CreateRewardPoolParams = {
  wallet: Wallet,
  user: UserInfo | null,
  counter: string;
  dividendsPerPool: number;
  nftCs: GYMintingPolicyId;
  poolCount: number;
  poolTn: GYTokenName;
  rewardValue: GYValue;
}

/** Supported wallets */
export type WalletType = "Nami" | "Eternl" | "VESPR" | "Lace" | "Tokeo";

/** Information of connected wallet */
export type Wallet = {
  /** Type of connected wallet */
  walletType: WalletType;
  /** User's public key hash */
  pkh: string;
  /** User's optional staking key hash */
  skh?: string;
  /** Bech32 address of wallet */
  bech32: string;
  /** Cbor hex of wallet address */
  hex: string;
  /** CIP 30 API of connected wallet */
  api: WalletApi;
};

export type Asset = {
  /** Asset in format `<currency symbol><hex(token name)>`*/
  unit: string;
  /** Amount of an asset */
  quantity: string;
};

/** Decoded datum attached to NFT marketplace UTxO */
export type MarketplaceDatum = {
  /** Share paid to the treasury */
  treasuryShare: number;
  /** Address of the treasury */
  treasury: string;
  /** Share paid to the protocol operator */
  operatorShare: number;
  /** Address of the protocol operator */
  operator: string;
};

/** Parameters used to buy an NFT from marketplace. */
export type BuyNftParams = {
  wallet: Wallet;
  user: UserInfo | null;
  /** NFT symbol to purchase. */
  currencySymbol: string;
};

export type NftMetadata = getMetadataPolicyIdTokenNameResponse["data"];

/** NFT that belongs to the user. */
export type Nft = {
  currencySymbol: string;
  tokenName: string;
  /** CIP-25 metadata attached to the minting transaction. */
  metadata: NftMetadata;
};

// User would just be a mapping of wallet address to MLM Soft account ID.
// We could include email, and other details, but probably best not to for
// privacy reasons.
export type User = {
  walletAddress: {
    pubKeyHash: string;
    stakeKeyHash: string;
  };
  mlmAccountId: number; // The user's account ID in MLM Soft
};

export type SignupParams = {
  wallet: WalletType; // which wallet to use in case user has multiple
  email: string;
  password: string;
  inviteCode: string; // The referral code
  firstName: string;
  lastName: string;
  adaAddress: string;
  mailingAddress: string;
  birthDate: string;
  postalCode: string;
};

export type SignupSuccess = {
  user: User;
};

export type KwarxsErrorCode =
  | "UserCancelled"
  | "ContractFailed"
  | "WalletNotConnected"
  | "OtherFailure";

export type KwarxsError = {
  code: KwarxsErrorCode;
  msg: string;
};

export type SignupError = {
  error: string;
};

export type DefaultRequestParams = {
  addresses: GYAddress[];
  change: GYAddress;
  checkCollateral?: boolean;
  /** @pattern [0-9a-fA-F]+ */
  collateral: string;
};

export class UserCancelledError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UserCancelled";
  }
}

export class ContractFailedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ContractFailed";
  }
}

export class WalletNotConnectedError extends Error {
  constructor(message?: string) {
    super(message ?? "Wallet not connected");
    this.name = "WalletNotConnected";
  }
}

export class UnsupportedWalletError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UnsupportedWallet";
  }
}

// This just expands on the auto-generated RewardPool type that is missing
// the poolUTxO details currently.
export type RewardPoolWithUTxO = RewardPool & {
  poolUTxO: [
    string,
    Record<string, number>,
    {
      constructor: number;
      fields: [
        { bytes: string },
        { int: number },
        { bytes: string },
        {
          map: Array<{
            k: { bytes: string };
            v: {
              map: Array<{
                k: { bytes: string };
                v: { int: number };
              }>;
            };
          }>;
        }
      ];
    }
  ];
};

export type MlmType = "NFTEAM" | "KWARXS";
